import { AvatarNew } from '@platform/components';
import {
  isTypeBlogArticleQuote,
  isTypeBlogHtmlEmbed,
  TypeBlogArticle,
  TypeBlogHtmlEmbed,
} from '@platform/contentful';
import { Asset } from 'contentful';
import { Block, Inline, Text, Mark, helpers } from '@contentful/rich-text-types';
import Divider from './util/Divider';
import MetaTags from '../util/MetaTags';
import PublicationDateString, { PublishDateOrCreatedAt } from './blog/PublicationDateString';
import cx from 'classix';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import SocialImpactNews from '../util/SocialImpactNews';

const AUTHOR_AVATARS: Record<string, string> = {
  // TODO: Upload to Azure
  'Alexandra Nemeth':
    'https://optimole.movingworlds.org/w:100/h:100/q:mauto/dpr:1.7/f:best/ig:avif/https://blog.movingworlds.org/wp-content/uploads/2022/09/Alexandra-Nemeth-Circle-Crop-Headshot.png',
  'Mark Horoszowski':
    'https://optimole.movingworlds.org/w:100/h:100/q:mauto/dpr:1.7/f:best/ig:avif/https://blog.movingworlds.org/wp-content/uploads/2025/01/Mark-Horoszowski_042321_013-square-headshot.jpg',
};

const AUTHOR_DESCRIPTIONS: Record<string, string> = {
  'Alexandra Nemeth': 'Senior Manager, Content Marketing & Storytelling at MovingWorlds',
  'Mark Horoszowski': 'Co-founder and CEO of MovingWorlds.org.',
};

type RichTextDocument = TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>['fields']['content'];

const ContentfulBlogArticle = ({
  articleEntry,
}: {
  articleEntry: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
}) => {
  const pageData = useContentfulLiveUpdates(articleEntry);

  // The articles seem to assume only a single category despite what was supported by Wordpress
  const category = pageData.fields.categories[0];

  return (
    <>
      <MetaTags
        title={pageData.fields.metaTitle}
        description={pageData.fields.metaDescription}
        imageUrl={pageData.fields.bannerImage?.fields.file?.url}
      />
      <div className='flex flex-col items-center sm:-mt-12 sm:pb-12 bg-white text-gray-500'>
        {/*<LogoTopBkg />*/}
        <div className='mx-auto z-10 max-w-[868px] sm:mb-0 sm:min-h-[450px] sm:pb-16 sm:mx-10  bg-white p-4 sm:p-8'>
          <h1 className='my-3 font-header text-2xl sm:text-4xl leading-7 sm:leading-11 font-semibold text-gray-500'>
            {pageData.fields.title}
          </h1>
          {pageData.fields.author && (
            <div className='flex flex-row h-full gap-2.5 items-center'>
              <div className='shrink-0 w-[100px] h-[100px]'>
                <AvatarNew
                  fallbackIcon='user-01'
                  url={AUTHOR_AVATARS?.[pageData.fields.author] ?? ''}
                />
              </div>
              <div className='h-fit flex flex-col justify-between font-header text-xs leading-6 text-gray-500'>
                <span className='text-base'>{pageData.fields.author}</span>
                {AUTHOR_DESCRIPTIONS[pageData.fields.author] && (
                  <span>{AUTHOR_DESCRIPTIONS[pageData.fields.author]}</span>
                )}
                <PublicationDateString articleEntry={pageData as PublishDateOrCreatedAt} />
              </div>
            </div>
          )}
          <div className='my-8 flex flex-row gap-2 blue-links text-gray-400'>
            <a href={import.meta.env['VITE_STATIC_FILES_URL'] + '/blog'}>Home</a>
            <span>&gt;&gt;</span>
            {category && (
              <>
                <a href={`/blog/category/${category.fields.slug}`}>{category.fields.name}</a>
              </>
            )}
          </div>
          <div className='w-full grow shrink-0 sm:col-span-8'>
            <ContentfulRichText document={pageData.fields.content} />
          </div>
        </div>
        <SocialImpactNews backgroundColor='blue' />
      </div>
    </>
  );
};

/** Renders the rich text blog content. Note that Contentful is very opinionated on the structure of these nodes, and various kinds of block nodes
 * can only render certain kinds of content. You can click into the types of @contentful/rich-text-types in the import in this file and then
 * click into nodeTypes.d.ts to see the type breakdown.
 *
 * *Most* nodes are handled here, but some have been left bare and/or oddly formatted. Generally the unformatted nodes are ones that are unused or intentionally
 * unsupported for being in very old Blog pages where we only care that the content is indexed, not viewed by users. */
const ContentfulRichText = ({ document }: { document: RichTextDocument }) => {
  return (
    <div className='blue-links'>
      {document.content.map((topLevelBlock, i) => (
        <BlockNode key={`${topLevelBlock.nodeType}-${i}`} node={topLevelBlock} isFirst={i === 0} />
      ))}
    </div>
  );
};

const ContentNode = ({ node }: { node: Block | Inline | Text }) => {
  if (helpers.isBlock(node)) {
    return <BlockNode node={node} />;
  }
  if (helpers.isInline(node)) {
    return <InlineNode node={node} />;
  }

  if (helpers.isText(node)) {
    return <TextNode node={node} />;
  }
};

const ContentNodes = ({ nodes }: { nodes: (Block | Inline | Text)[] }) => {
  return nodes.map((child, index) => <ContentNode key={index} node={child} />);
};

const BlockNode = ({
  node,
  isFirst,
}: {
  node: Block;
  // Special case for the first document item; see EmbedAssetBlock
  isFirst?: boolean;
}) => {
  const contentNodes = <ContentNodes nodes={node.content} />;
  switch (node.nodeType) {
    case 'document':
      return <div>Document block</div>;
    case 'paragraph':
      return <p className='mb-4'>{contentNodes}</p>;
    case 'heading-1':
      return <h1>{contentNodes}</h1>;
    case 'heading-2':
      return (
        <h2 className='mt-10 mb-3 font-semibold text-gray-500 text-2xl leading-7'>
          {contentNodes}
        </h2>
      );
    case 'heading-3':
      return (
        <h3 className='mt-10 mb-2 text-xl leading-7 font-semibold text-gray-500'>{contentNodes}</h3>
      );
    case 'heading-4':
      return <h4 className='mt-10 mb-3 text-lg leading-5 font-semibold'>{contentNodes}</h4>;
    case 'heading-5':
      return <h5>{contentNodes}</h5>;
    case 'heading-6':
      return <h6>{contentNodes}</h6>;
    case 'ordered-list':
      return <ol className='ml-8 list-decimal flex flex-col'>{contentNodes}</ol>;
    case 'unordered-list':
      return <ul className='ml-8 list-disc flex flex-col'>{contentNodes}</ul>;
    case 'list-item':
      return <li>{contentNodes}</li>;
    case 'hr':
      return <Divider />;
    case 'blockquote':
      return <blockquote>{contentNodes}</blockquote>;
    case 'embedded-entry-block':
      return (
        <EmbedEntryBlock
          embeddedEntry={
            node.data.target as TypeBlogHtmlEmbed<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>
          }
        />
      );
    case 'embedded-asset-block':
      return (
        <EmbedAssetBlock
          asset={node.data.target as Asset<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>}
          isFirst={isFirst}
        />
      );
    case 'embedded-resource-block':
      return <div>{contentNodes}</div>;
    case 'table':
      return <table>{contentNodes}</table>;
    case 'table-row':
      return <tr>{contentNodes}</tr>;
    case 'table-cell':
      return <td>{contentNodes}</td>;
    case 'table-header-cell':
      return <th>{contentNodes}</th>;
    default:
      return null;
  }
};

const InlineNode = ({ node }: { node: Inline }) => {
  switch (node.nodeType) {
    case 'asset-hyperlink':
      return '<TODO: asset-hyperlink>';
    case 'embedded-entry-inline':
      return '<TODO: embedded-entry-inline>';
    case 'embedded-resource-inline':
      return '<TODO: embedded-resource-inline>';
    case 'entry-hyperlink':
      return '<TODO: entry-hyperlink>';
    case 'hyperlink':
      return (
        <a href={node.data.uri} target='_blank' rel='noopener noreferrer'>
          {node.content.map((child, index) => (
            <ContentNode key={index} node={child} />
          ))}
        </a>
      );
    case 'resource-hyperlink':
      return '<TODO: resource-hyperlink>';
    default:
      throw new Error(`Unhandled inline node type: ${node.nodeType}`);
  }
};

const TextNode = ({ node }: { node: Text }) => {
  switch (node.nodeType) {
    case 'text':
      if (!node.marks.length) {
        return node.value;
      } else {
        return <MarksNode node={node} remainingMarks={node.marks} />;
      }
    default:
      throw new Error(`Unhandled text node type: ${node.nodeType}`);
  }
};

const MarksNode = ({ node, remainingMarks }: { node: Text; remainingMarks: Mark[] }) => {
  if (remainingMarks.length === 0) {
    return <>{node.value}</>;
  }
  const [currentMark, ...restMarks] = remainingMarks;
  const n = <MarksNode node={node} remainingMarks={restMarks} />;
  switch (currentMark.type) {
    case 'bold':
      return <strong className='font-semibold'>{n}</strong>;
    case 'italic':
      return <em className='italic'>{n}</em>;
    case 'underline':
      return <span className='underline'>{n}</span>;
    case 'code':
      // Entirely unsupported and not useful in the blog. Instead, code blocks are currently used to record errors during
      // migration from Wordpress, so we just skip rendering them.
      return <div />;
    case 'superscript':
      return <sup>{n}</sup>;
    case 'subscript':
      return <sub>{n}</sub>;
    case 'strikethrough':
      return <span className='line-through'>{n}</span>;
    default:
      throw new Error(`Unhandled mark type: ${currentMark.type}`);
  }
};

const EmbedEntryBlock = ({
  embeddedEntry,
}: {
  embeddedEntry: TypeBlogHtmlEmbed<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
}) => {
  if (isTypeBlogArticleQuote(embeddedEntry)) {
    // Blog quotes allow rendering full Rich Text content because the legacy Wordpress pages were VERY liberal and inconsistent with
    // block quote formatting, using it for generic emphasis
    return (
      // Yellow left-border for emphasis
      <div className='flex flex-col border-l-4 border-accent-500 pl-4 my-6 italic text-lg'>
        <ContentfulRichText document={embeddedEntry.fields.content as RichTextDocument} />
        {embeddedEntry.fields.quotee && (
          <div className='ml-auto'>{`— ${embeddedEntry.fields.quotee}`}</div>
        )}
      </div>
    );
  }

  if (isTypeBlogHtmlEmbed(embeddedEntry)) {
    return (
      <iframe
        src={embeddedEntry.fields.url}
        style={{ width: 459, height: 261 }}
        className='aspect-auto mx-auto my-6'
      />
    );
  }

  // @ts-expect-error Should be unreachable if all entry types are handled
  throw new Error(`Unhandled embedded entry type: ${embeddedEntry.sys.contentType.sys.id}`);
};

const EmbedAssetBlock = ({
  asset,
  isFirst,
}: {
  asset: Asset<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>;
  // Special case for if the very first Rich Text child item is an embedded asset. If so, and if it's an image, we wrap
  // the following text around it by applying a float style.
  isFirst?: boolean;
}) => {
  switch (asset.fields.file?.contentType) {
    case 'image/png':
    case 'image/jpeg':
    case 'image/jpg':
    case 'image/gif':
    case 'image/webp':
    case 'image/svg+xml':
      return (
        <div className={cx(isFirst ? 'float-right ml-6 mb-6' : 'my-6 sm:max-w-4/5 mx-auto')}>
          <a href={asset.fields.file.url} target='_blank' rel='noopener noreferrer'>
            <img src={asset.fields.file.url} className='max-w-full h-auto' />
          </a>
        </div>
      );
    default:
      throw new Error(`Unhandled embedded asset content type: ${asset.fields.file?.contentType}`);
  }
};

export default ContentfulBlogArticle;
